import { useState, useMemo } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import {
    ArrowLeft,
    Package,
    SearchLg,
    Eye,
    User01,
    Edit05,
    CheckVerified01,
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Button } from '@/components/base/buttons/button';
import { Table, TableCard } from '@/components/application/table/table';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { DistributionChart } from '@/components/application/charts/distribution-chart';
import { getCompany } from '@/data/api/companies';
import { getReceiverStats, getReceiverContainers } from '@/data/api/receivers';
import type { Container, ContainerStatus, RiskLevel, DocumentStatus } from '@/types/container';
import type { Company } from '@/types/company';
import { useTranslation } from 'react-i18next';
import { useI18n } from '@/hooks/use-i18n';
import { useQuery } from '@tanstack/react-query';
import { useMe } from '@/hooks/api/use-auth';
import { useDocumentArrangements } from '@/hooks/api/use-document-arrangements';
import { useUploadDocument } from '@/hooks/api/use-documents';
import { BackLink } from '@/components/base/back-link/back-link';
import { ShipperOnboardingHeaderAction } from '@/components/application/onboarding/shipper-onboarding-context';
import { useToast } from '@/hooks/use-toast';
import { useValidateCompany, useUnvalidateCompany } from '@/hooks/api/use-companies';

// Company type badge colors
const getCompanyTypeBadgeColor = (type: string | undefined) => {
    switch (type?.toLowerCase()) {
        case 'receiver':
            return 'brand';
        case 'freight_forwarder':
            return 'success';
        case 'carrier':
            return 'warning';
        case 'customs_broker':
            return 'error';
        case 'sender':
            return 'gray';
        default:
            return 'gray';
    }
};

// Format company type for display
const formatCompanyType = (type: string | undefined) => {
    if (!type) return '-';
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export const BusinessPartnerDetailPage = () => {
    const { t } = useTranslation(['pages:receivers', 'pages:containers', 'types', 'common']);
    const { translateContainerStatus } = useI18n();
    const location = useLocation();
    const navigate = useNavigate();
    const { id: partnerId } = useParams<{ id: string }>();
    const { data: currentUser } = useMe();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<ContainerStatus | ''>('');
    const [riskLevelFilter, setRiskLevelFilter] = useState<RiskLevel | ''>('');
    const [documentStatusFilter, setDocumentStatusFilter] = useState<DocumentStatus | ''>('');

    // Fetch partner company info
    const { data: partner, isLoading: isLoadingPartner, error: partnerError } = useQuery<Company>({
        queryKey: ['business-partner', partnerId],
        queryFn: async () => {
            if (!partnerId) throw new Error('Partner ID is required');
            return await getCompany(partnerId);
        },
        enabled: !!partnerId,
        retry: 2,
    });

    // Fetch partner stats
    const { data: stats, isLoading: isLoadingStats } = useQuery({
        queryKey: ['business-partner-stats', currentUser?.company_id, partnerId],
        queryFn: async () => {
            if (!currentUser?.company_id || !partnerId) return null;
            return await getReceiverStats(currentUser.company_id, partnerId);
        },
        enabled: !!currentUser?.company_id && !!partnerId,
        staleTime: 0,
    });

    // Fetch partner containers
    const { data: containers = [], isLoading: isLoadingContainers } = useQuery<Container[]>({
        queryKey: ['business-partner-containers', currentUser?.company_id, partnerId, searchTerm, statusFilter, riskLevelFilter, documentStatusFilter],
        queryFn: async () => {
            if (!currentUser?.company_id || !partnerId) return [];
            return await getReceiverContainers(
                currentUser.company_id,
                partnerId,
                {
                    search: searchTerm || undefined,
                    status: statusFilter || undefined,
                    riskLevel: riskLevelFilter || undefined,
                    documentStatus: documentStatusFilter || undefined,
                }
            );
        },
        enabled: !!currentUser?.company_id && !!partnerId,
        staleTime: 0,
    });

    // Fetch document arrangements (only for receivers)
    const { data: arrangementsData } = useDocumentArrangements(
        partner?.company_name,
        partnerId,
        !!partner && partner.company_type === 'receiver',
    );

    const uploadDocument = useUploadDocument();
    const toast = useToast();
    const validateCompany = useValidateCompany();
    const unvalidateCompany = useUnvalidateCompany();

    const isLoading = isLoadingPartner || isLoadingStats || isLoadingContainers;

    const statusOptions = [
        { id: '', label: t('list.filters.allStatuses', { ns: 'pages:containers' }) },
        { id: 'In Transit', label: t('containerStatus.inTransit', { ns: 'types' }) },
        { id: 'Delivered', label: t('containerStatus.delivered', { ns: 'types' }) },
        { id: 'Delayed', label: t('containerStatus.delayed', { ns: 'types' }) },
        { id: 'At Port', label: t('containerStatus.atPort', { ns: 'types' }) }
    ];

    const riskLevelOptions = [
        { id: '', label: t('list.filters.allLevels', { ns: 'pages:containers' }) },
        { id: 'Low', label: t('riskLevel.low', { ns: 'types' }) },
        { id: 'Medium', label: t('riskLevel.medium', { ns: 'types' }) },
        { id: 'High', label: t('riskLevel.high', { ns: 'types' }) },
        { id: 'Critical', label: t('riskLevel.critical', { ns: 'types' }) }
    ];

    const documentStatusOptions = [
        { id: '', label: t('list.filters.allStatuses', { ns: 'pages:containers' }) },
        { id: 'Completo', label: t('documentStatus.complete', { ns: 'types' }) },
        { id: 'Pending', label: t('documentStatus.pending', { ns: 'types' }) },
        { id: 'Error', label: t('documentStatus.error', { ns: 'types' }) }
    ];

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'In Transit': return 'brand';
            case 'Delivered': return 'success';
            case 'Delayed': return 'error';
            case 'At Port': return 'warning';
            default: return 'gray';
        }
    };

    // Navigate to address book filtered by company (ID-based)
    const handleSeePeople = () => {
        if (partner) {
            navigate(`/shipper/address-book?contact_company_id=${partner.company_id}&company=${encodeURIComponent(partner.company_name)}`, { state: navigationStateFrom(location) });
        }
    };

    // Calculate distribution data for charts from stats
    const distributionData = useMemo(() => {
        if (!stats) {
            return {
                total: 0,
                riskData: [],
                statusData: [],
                documentData: [],
            };
        }

        return {
            total: stats.total,
            riskData: [
                { label: t('riskLevel.low', { ns: 'types' }), value: stats.riskDistribution.Low, color: '#10B981' },
                { label: t('riskLevel.medium', { ns: 'types' }), value: stats.riskDistribution.Medium, color: '#F59E0B' },
                { label: t('riskLevel.high', { ns: 'types' }), value: stats.riskDistribution.High, color: '#EF4444' },
                { label: t('riskLevel.critical', { ns: 'types' }), value: stats.riskDistribution.Critical, color: '#DC2626' },
            ],
            statusData: [
                { label: t('containerStatus.draft', { ns: 'types' }), value: stats.statusDistribution.Draft, color: '#6B7280' },
                { label: t('containerStatus.booked', { ns: 'types' }), value: stats.statusDistribution.Booked, color: '#3B82F6' },
                { label: t('containerStatus.inTransit', { ns: 'types' }), value: stats.statusDistribution['In Transit'], color: '#3B82F6' },
                { label: t('containerStatus.arrived', { ns: 'types' }), value: stats.statusDistribution.Arrived, color: '#F59E0B' },
                { label: t('containerStatus.delivered', { ns: 'types' }), value: stats.statusDistribution.Delivered, color: '#10B981' },
                { label: t('containerStatus.onHold', { ns: 'types' }), value: stats.statusDistribution['On Hold'], color: '#F59E0B' },
                { label: t('containerStatus.cancelled', { ns: 'types' }), value: stats.statusDistribution.Cancelled, color: '#EF4444' },
            ],
            documentData: [
                { label: t('documentStatus.complete', { ns: 'types' }), value: stats.documentDistribution.Completo, color: '#10B981' },
                { label: t('documentStatus.pending', { ns: 'types' }), value: stats.documentDistribution.Pending, color: '#F59E0B' },
                { label: t('documentStatus.error', { ns: 'types' }), value: stats.documentDistribution.Error, color: '#EF4444' },
            ],
        };
    }, [stats, t]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('detail.loading', { ns: 'pages:receivers' }) || 'Loading...'}</p>
            </div>
        );
    }

    if (!partner) {
        return (
            <div className="text-center py-12">
                <Package className="size-12 mx-auto mb-4 text-tertiary" />
                <h3 className="text-lg font-medium text-primary mb-2">
                    Partner not found
                </h3>
                <p className="text-tertiary mb-4">
                    {partnerError
                        ? `Error loading partner: ${(partnerError as Error).message}`
                        : 'The selected partner does not exist.'}
                </p>
                <BackLink
                    fallbackTo="/shipper/business-partners"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-solid px-4 py-2.5 text-md font-semibold dark:text-white shadow-xs-skeumorphic ring-1 ring-transparent ring-inset hover:bg-brand-solid_hover"
                >
                    <ArrowLeft className="size-5" />
                    {t('detail.backToList', { ns: 'pages:receivers', defaultValue: 'Back to list' })}
                </BackLink>
            </div>
        );
    }

    const address = partner.headquarters_address;
    const country = address?.country_code || null;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <BackLink fallbackTo="/shipper/business-partners" aria-label={t('detail.backToList', { ns: 'pages:receivers', defaultValue: 'Back to list' })} />
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-semibold text-primary">{partner.legal_name || partner.company_name}</h1>
                            <Badge color={getCompanyTypeBadgeColor(partner.company_type)} size="sm">
                                {formatCompanyType(partner.company_type)}
                            </Badge>
                            {partner.is_verified ? (
                                <button
                                    onClick={() => unvalidateCompany.mutate(partnerId!)}
                                    className="inline-flex items-center gap-1 rounded-full bg-success-50 px-2.5 py-0.5 text-xs font-medium text-success-700 hover:bg-success-100 transition-colors"
                                    title={`Verified${partner.verified_at ? ` on ${new Date(partner.verified_at).toLocaleDateString('it-IT')}` : ''}`}
                                >
                                    <CheckVerified01 className="size-3.5" />
                                    Verified
                                </button>
                            ) : (
                                <button
                                    onClick={() => validateCompany.mutate(partnerId!)}
                                    className="inline-flex items-center gap-1 rounded-full border border-secondary px-2.5 py-0.5 text-xs font-medium text-tertiary hover:bg-secondary transition-colors"
                                >
                                    <CheckVerified01 className="size-3.5" />
                                    Validate
                                </button>
                            )}
                        </div>
                        {(country || partner.primary_email) && (
                            <p className="text-tertiary mt-1">
                                {[country, partner.primary_email].filter(Boolean).join(' • ')}
                            </p>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <ShipperOnboardingHeaderAction />
                    <Button
                        color="secondary"
                        iconLeading={Edit05}
                        onClick={() => navigate(`/shipper/business-partners/${partnerId}/edit`, { state: navigationStateFrom(location) })}
                    >
                        Edit
                    </Button>
                    <Button
                        color="secondary"
                        iconLeading={User01}
                        onClick={handleSeePeople}
                    >
                        {t('detail.seePeople', { defaultValue: 'See People' })}
                    </Button>
                </div>
            </div>

            {/* Partner Info */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Company Info */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-4">
                        {t('detail.info.companyInfo', { ns: 'pages:receivers', defaultValue: 'Company Information' })}
                    </h2>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-sm font-medium text-primary">
                                    {t('detail.info.fields.legalName', { ns: 'pages:receivers', defaultValue: 'Legal Name' })}
                                </div>
                                <div className="text-sm text-tertiary">{partner.legal_name || partner.company_name}</div>
                            </div>
                            <div>
                                <div className="text-sm font-medium text-primary">
                                    {t('detail.info.fields.entityType', { ns: 'pages:receivers', defaultValue: 'Company Type' })}
                                </div>
                                <div className="text-sm text-tertiary">{formatCompanyType(partner.company_type)}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <div className="text-sm font-medium text-primary">Tax ID (VAT)</div>
                                <div className="text-sm text-tertiary">{partner.tax_id || '-'}</div>
                            </div>
                            <div>
                                <div className="text-sm font-medium text-primary">EORI Number</div>
                                <div className="text-sm text-tertiary">{partner.eori_number || '-'}</div>
                            </div>
                            <div>
                                <div className="text-sm font-medium text-primary">
                                    {t('detail.info.fields.country', { ns: 'pages:receivers', defaultValue: 'Country' })}
                                </div>
                                <div className="text-sm text-tertiary">{country || '-'}</div>
                            </div>
                        </div>
                        {address && (
                            <div>
                                <div className="text-sm font-medium text-primary">
                                    {t('detail.info.fields.address', { ns: 'pages:receivers', defaultValue: 'Address' })}
                                </div>
                                <div className="text-sm text-tertiary">
                                    {[address.street, address.city, address.state_province, address.postal_code].filter(Boolean).join(', ')}
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-sm font-medium text-primary">
                                    {t('detail.info.fields.email', { ns: 'pages:receivers', defaultValue: 'Email' })}
                                </div>
                                <div className="text-sm text-tertiary">{partner.primary_email || '-'}</div>
                            </div>
                            <div>
                                <div className="text-sm font-medium text-primary">
                                    {t('detail.info.fields.phone', { ns: 'pages:receivers', defaultValue: 'Phone' })}
                                </div>
                                <div className="text-sm text-tertiary">{partner.primary_phone || 'N/A'}</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Container Stats */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <h2 className="text-lg font-semibold text-primary mb-4">
                        {t('detail.info.containerStats', { ns: 'pages:receivers', defaultValue: 'Container Statistics' })}
                    </h2>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-primary">{stats?.total || 0}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.total', { ns: 'pages:receivers', defaultValue: 'Total' })}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-brand-600">{stats?.inTransit || 0}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.inTransit', { ns: 'pages:receivers', defaultValue: 'In Transit' })}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-success-600">{stats?.delivered || 0}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.delivered', { ns: 'pages:receivers', defaultValue: 'Delivered' })}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-error-600">{stats?.delayed || 0}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.delayed', { ns: 'pages:receivers', defaultValue: 'Delayed' })}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-warning-600">{stats?.pendingDocuments || 0}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.pendingDocuments', { ns: 'pages:receivers', defaultValue: 'Pending Docs' })}</div>
                            </div>
                            <div className="text-center">
                                <div className="text-2xl font-semibold text-error-600">{stats?.highRisk || 0}</div>
                                <div className="text-sm text-tertiary">{t('detail.info.stats.highRisk', { ns: 'pages:receivers', defaultValue: 'High Risk' })}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Document Arrangements — receivers only */}
            {partner.company_type === 'receiver' && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold text-primary">Document Arrangements</h2>
                        {/* Upload dropzone */}
                        <label className="cursor-pointer inline-flex items-center gap-2 rounded-lg border border-secondary px-4 py-2 text-sm font-medium text-primary hover:bg-secondary transition-colors">
                            <input
                                type="file"
                                accept=".pdf"
                                className="hidden"
                                onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file || !partnerId) return;
                                    try {
                                        await uploadDocument.mutateAsync({
                                            file,
                                            documentType: 'document_arrangement',
                                            receiverCompanyId: partnerId,
                                        });
                                        toast.success('Document uploaded — AI extraction in progress');
                                    } catch (err: any) {
                                        toast.error(err.message || 'Upload failed');
                                    }
                                    e.target.value = '';
                                }}
                                disabled={uploadDocument.isPending}
                            />
                            {uploadDocument.isPending ? 'Uploading...' : 'Upload Arrangement PDF'}
                        </label>
                    </div>
                    {arrangementsData?.arrangements && arrangementsData.arrangements.length > 0 ? (
                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                            {arrangementsData.arrangements.map((arr) => (
                                <div key={arr.arrangement_id} className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-sm font-semibold text-primary">
                                            {arr.destination_port_name || 'No port specified'}
                                        </h3>
                                        {arr.destination_country_code && (
                                            <Badge color="gray" size="sm">{arr.destination_country_code}</Badge>
                                        )}
                                    </div>
                                    <div className="space-y-3">
                                        {arr.notify_parties.length > 0 && (
                                            <div>
                                                <div className="text-xs font-medium text-tertiary uppercase tracking-wider mb-1">Notify Parties</div>
                                                <div className="space-y-1">
                                                    {arr.notify_parties.map((np, i) => (
                                                        <div key={i} className="text-sm text-primary">
                                                            {np.name}{np.company ? ` — ${np.company}` : ''}{np.email ? ` (${np.email})` : ''}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {arr.document_recipients.length > 0 && (
                                            <div>
                                                <div className="text-xs font-medium text-tertiary uppercase tracking-wider mb-1">Document Recipients</div>
                                                <div className="space-y-1">
                                                    {arr.document_recipients.map((dr, i) => (
                                                        <div key={i} className="text-sm text-primary">
                                                            {dr.name || dr.email}{dr.purpose ? ` — ${dr.purpose}` : ''}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {arr.stakeholders.length > 0 && (
                                            <div>
                                                <div className="text-xs font-medium text-tertiary uppercase tracking-wider mb-1">Stakeholders</div>
                                                <div className="space-y-1">
                                                    {arr.stakeholders.map((sh, i) => (
                                                        <div key={i} className="text-sm text-primary">
                                                            {sh.name}{sh.type ? ` (${sh.type})` : ''}{sh.role ? ` — ${sh.role}` : ''}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {arr.special_instructions && (
                                            <div>
                                                <div className="text-xs font-medium text-tertiary uppercase tracking-wider mb-1">Special Instructions</div>
                                                <div className="text-sm text-primary">{arr.special_instructions}</div>
                                            </div>
                                        )}
                                        {arr.notify_parties.length === 0 && arr.document_recipients.length === 0 && arr.stakeholders.length === 0 && !arr.special_instructions && (
                                            <div className="text-sm text-tertiary">No details configured yet</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="rounded-xl bg-secondary/50 p-8 text-center">
                            <p className="text-sm text-tertiary">
                                No arrangements yet. Upload a PDF to auto-extract arrangement data.
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Distribution Charts */}
            {stats && stats.total > 0 && (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <DistributionChart
                        title={t('list.charts.riskLevels', { ns: 'pages:containers', defaultValue: 'Risk Level Distribution' })}
                        data={distributionData.riskData}
                        total={distributionData.total}
                    />
                    <DistributionChart
                        title={t('list.charts.containerStatus', { ns: 'pages:containers', defaultValue: 'Container Status Distribution' })}
                        data={distributionData.statusData}
                        total={distributionData.total}
                    />
                    <DistributionChart
                        title={t('list.charts.documentStatus', { ns: 'pages:containers', defaultValue: 'Document Status Distribution' })}
                        data={distributionData.documentData}
                        total={distributionData.total}
                    />
                </div>
            )}

            {/* Container List */}
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-primary">{t('detail.containers.title', { ns: 'pages:receivers', defaultValue: 'Containers' })}</h2>
                    <div className="text-sm text-tertiary">
                        {containers.length} {t('detail.containers.count', { ns: 'pages:receivers', defaultValue: 'of' })} {stats?.total || 0} {t('list.title', { ns: 'pages:containers', defaultValue: 'containers' })}
                    </div>
                </div>

                {/* Filters */}
                <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <Input
                            placeholder={t('detail.containers.searchPlaceholder', { ns: 'pages:receivers', defaultValue: 'Search containers...' })}
                            value={searchTerm}
                            onChange={setSearchTerm}
                            icon={SearchLg}
                            size="md"
                        />

                        <Select
                            placeholder={t('detail.containers.statusPlaceholder', { ns: 'pages:receivers', defaultValue: 'Filter by status' })}
                            items={statusOptions}
                            selectedKey={statusFilter}
                            onSelectionChange={(key) => setStatusFilter(key as ContainerStatus | '')}
                            size="md"
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>

                        <Select
                            placeholder={t('detail.containers.riskPlaceholder', { ns: 'pages:receivers', defaultValue: 'Filter by risk' })}
                            items={riskLevelOptions}
                            selectedKey={riskLevelFilter}
                            onSelectionChange={(key) => setRiskLevelFilter(key as RiskLevel | '')}
                            size="md"
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>

                        <Select
                            placeholder={t('detail.containers.documentsPlaceholder', { ns: 'pages:receivers', defaultValue: 'Filter by documents' })}
                            items={documentStatusOptions}
                            selectedKey={documentStatusFilter}
                            onSelectionChange={(key) => setDocumentStatusFilter(key as DocumentStatus | '')}
                            size="md"
                        >
                            {(item) => (
                                <Select.Item key={item.id} id={item.id}>
                                    {item.label}
                                </Select.Item>
                            )}
                        </Select>
                    </div>
                </div>

                {/* Containers Table */}
                {containers.length > 0 ? (
                    <TableCard.Root>
                        <Table>
                            <Table.Header>
                                <Table.Head label={t('list.table.headers.containerNumber', { ns: 'pages:containers' })} isRowHeader />
                                <Table.Head label={t('list.table.headers.route', { ns: 'pages:containers' })} />
                                <Table.Head label={t('list.table.headers.progress', { ns: 'pages:containers' })} />
                                <Table.Head label="ETA" />
                                <Table.Head label={t('list.table.headers.status', { ns: 'pages:containers' })} />
                                <Table.Head label={t('list.table.headers.actions', { ns: 'pages:containers' })} />
                            </Table.Header>
                            <Table.Body>
                                {containers.map((container) => (
                                    <Table.Row key={container.id}>
                                        <Table.Cell>
                                            <div className="font-medium text-primary">{container.containerNumber}</div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="text-sm text-tertiary">
                                                {container.route.from} → {container.route.to}
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <div className="flex items-center gap-2">
                                                <ProgressBar
                                                    value={container.progress}
                                                    min={0}
                                                    max={100}
                                                />
                                                <span className="text-sm text-tertiary">{container.progress}%</span>
                                            </div>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="text-sm text-tertiary">
                                                {container.eta?.toLocaleDateString('it-IT')}
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Badge color={getStatusColor(container.status)} size="sm">
                                                {translateContainerStatus(container.status)}
                                            </Badge>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Link
                                                to={`/shipper/containers/${container.id}`}
                                                className="text-brand-secondary hover:text-brand-secondary_hover"
                                            >
                                                <Eye className="size-4" />
                                            </Link>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </TableCard.Root>
                ) : (
                    <div className="text-center py-12">
                        <div className="text-tertiary mb-4">
                            <Package className="size-12 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-primary mb-2">
                                {t('detail.containers.noContainers.title', { ns: 'pages:receivers', defaultValue: 'No containers found' })}
                            </h3>
                            <p className="text-tertiary">
                                {searchTerm || statusFilter || riskLevelFilter || documentStatusFilter
                                    ? t('detail.containers.noContainers.message', { ns: 'pages:receivers', defaultValue: 'Try adjusting your filters' })
                                    : t('detail.containers.noContainers.emptyMessage', { ns: 'pages:receivers', defaultValue: 'No containers for this partner' })
                                }
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
